import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi, useMutation } from '../hooks/useApi';
import { upgradeRequestAPI, adminAPI } from '../utils/api';
import {
  ChartBarIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  TrashIcon,
  UserPlusIcon,
  ArrowUturnLeftIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import AcceptanceDocument from './AcceptanceDocument';
import ChangePasswordModal from './ChangePasswordModal';
import DeleteRequestModal from './DeleteRequestModal';
import CreateAdminModal from './CreateAdminModal';
import DeletedRequestsTab from './DeletedRequestsTab';
import MitraManagementModal from './MitraManagementModal';
import PackageManagementModal from './PackageManagementModal';
import { notify } from '../utils/notifications';
import { formatDate } from '../utils/dateUtils';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(null);
  const [showDocument, setShowDocument] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [showMitraManagement, setShowMitraManagement] = useState(false);
  const [showPackageManagement, setShowPackageManagement] = useState(false);
  const [deletedCurrentPage, setDeletedCurrentPage] = useState(1);
  const [deletedSearch, setDeletedSearch] = useState('');
  const [debouncedDeletedSearch, setDebouncedDeletedSearch] = useState('');
  
  // Check if user needs to change password on mount
  useEffect(() => {
    if (user && !user.password_changed) {
      setForcePasswordChange(true);
      setShowChangePassword(true);
    } else if (user && user.password_changed) {
      setForcePasswordChange(false);
    }
  }, [user]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Debounce deleted search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDeletedSearch(deletedSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [deletedSearch]);

  const { data: stats, loading: statsLoading, error: statsError, refetch: refetchStats } = useApi(
    adminAPI.getDashboardStats,
    [], // Remove activeTab dependency to prevent unnecessary calls
    { immediate: true } // Explicitly enable immediate loading
  );

  const { data: requestsData, loading: requestsLoading, refetch } = useApi(
    () => upgradeRequestAPI.getAll({
      search: debouncedSearch || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,  
      page: currentPage,
      limit: 10
    }),
    [debouncedSearch, statusFilter, currentPage], 
    { immediate: true } // Load immediately to test
  );

  const { data: deletedRequestsData, loading: deletedRequestsLoading, refetch: refetchDeleted } = useApi(
    () => upgradeRequestAPI.getDeleted({
      search: debouncedDeletedSearch || undefined,
      page: deletedCurrentPage,
      limit: 10
    }),
    [debouncedDeletedSearch, deletedCurrentPage], 
    { immediate: activeTab === 'deleted' }
  );

  // Load deleted requests when switching to deleted tab
  useEffect(() => {
    if (activeTab === 'deleted') {
      refetchDeleted();
    }
  }, [activeTab, refetchDeleted]);

  const { mutate: updateRequest, loading: updateLoading } = useMutation(
    ({ id, data }) => upgradeRequestAPI.update(id, data),
    {
      onSuccess: (response) => {
        const status = response.data.status;
        const message = status === 'approved'
          ? 'Request approved successfully!'
          : status === 'rejected'
          ? 'Request rejected successfully!'
        
          : 'Request updated successfully!';
        refetch();
        setSelectedRequest(null);
        setShowDetails(null);
        // Only refetch stats if on overview tab
        if (activeTab === 'overview') {
          refetchStats();
        }
      },
      onError: (error) => {
        console.error('Update request error:', error);
        notify.error('Failed to update request. Please try again.');
      }
    }
  );

  const { mutate: deleteRequest, loading: deleteLoading } = useMutation(
    ({ id, reason }) => upgradeRequestAPI.delete(id, reason),
    {
      onSuccess: () => {
        notify.success('Request deleted successfully!');
        refetch();
        setShowDeleteModal(false);
        setRequestToDelete(null);
        if (activeTab === 'overview') {
          refetchStats();
        }
      },
      onError: (error) => {
        console.error('Delete request error:', error);
        notify.error('Failed to delete request. Please try again.');
      }
    }
  );

  const { mutate: restoreRequest, loading: restoreLoading } = useMutation(
    (id) => upgradeRequestAPI.restore(id),
    {
      onSuccess: () => {
        notify.success('Request restored successfully!');
        refetchDeleted();
        if (activeTab === 'overview') {
          refetchStats();
        }
      },
      onError: (error) => {
        console.error('Restore request error:', error);
        notify.error('Failed to restore request. Please try again.');
      }
    }
  );

  const handleStatusUpdate = async (requestId, status, comments = '') => {
    try {
      const updateData = {
        status,
        admin_comments: comments || undefined
      };

      if (status === 'approved') {
        updateData.circuit_id = `CID${Date.now()}`;
        updateData.circuit_ready_date = new Date().toISOString().split('T')[0];
        updateData.acceptance_date = new Date().toISOString().split('T')[0];
      }

      await updateRequest({ id: requestId, data: updateData });
    } catch (error) {
      console.error('Error updating request status:', error);
      notify.error('Failed to update request status. Please try again.');
    }
  };

  const handleViewDocument = async (request) => {
    if (request.status !== 'approved') {
      notify.warning('Document is only available for approved requests.');
      return;
    }

    try {
      const response = await upgradeRequestAPI.getAcceptanceDocument(request.id);
      setSelectedRequest({
        ...request,
        documentData: response.data.data
      });
      setShowDocument(true);
    } catch (error) {
      console.error('Error fetching document:', error);
      notify.error('Failed to load document. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const baseClass = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case 'pending':
        return `${baseClass} bg-yellow-100 text-yellow-800 border border-yellow-200`;
      case 'admin_approved':
      case 'approved':
        return `${baseClass} bg-blue-100 text-blue-800 border border-blue-200`;
      case 'waiting_noc':
        return `${baseClass} bg-indigo-100 text-indigo-800 border border-indigo-200`;
      case 'completed':
        return `${baseClass} bg-green-100 text-green-800 border border-green-200`;
      case 'rejected':
        return `${baseClass} bg-red-100 text-red-800 border border-red-200`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800 border border-gray-200`;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending Review',
      'admin_approved': 'Admin Approved',
      'approved': 'Approved',
      'waiting_noc': 'Awaiting NOC',
      'completed': 'Completed',
      'rejected': 'Rejected'
    };
    return labels[status] || status;
  };

  // Memoize refresh function to prevent recreation on every render
  const handleRefreshStats = useCallback(() => {
    refetchStats();
  }, [refetchStats]);

  const handlePasswordChangeClose = (success) => {
    if (success && forcePasswordChange) {
      setForcePasswordChange(false);
    }
    setShowChangePassword(false);
  };

  const handleDeleteRequest = (request) => {
    setRequestToDelete(request);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = (reason) => {
    if (requestToDelete) {
      deleteRequest({ id: requestToDelete.id, reason });
    }
  };

  const handleCreateAdminSuccess = () => {
    // Refresh stats if needed
    if (activeTab === 'overview') {
      refetchStats();
    }
  };

  const handleMitraManagementSuccess = () => {
    // Refresh stats if needed
    if (activeTab === 'overview') {
      refetchStats();
    }
  };

  const handleRestoreRequest = (requestId) => {
    if (window.confirm('Are you sure you want to restore this request?')) {
      restoreRequest(requestId);
    }
  };

  const overview = useMemo(() => {
    console.log('Stats data:', stats);
    return stats?.data?.overview || {};
  }, [stats]);
  const recentRequests = useMemo(() => stats?.data?.recentRequests || [], [stats]);
  const requests = useMemo(() => {
    return requestsData?.data?.requests || requestsData?.requests || [];
  }, [requestsData]);
  const pagination = useMemo(() => {
    return requestsData?.data?.pagination || requestsData?.pagination || {};
  }, [requestsData]);
  const deletedRequests = useMemo(() => {
    return deletedRequestsData?.data?.requests || deletedRequestsData?.requests || [];
  }, [deletedRequestsData]);
  const deletedPagination = useMemo(() => {
    return deletedRequestsData?.data?.pagination || deletedRequestsData?.pagination || {};
  }, [deletedRequestsData]);

  // Handle stats errors
  useEffect(() => {
    if (statsError) {
      console.error('Stats error:', statsError);
      notify.error('Failed to load dashboard statistics');
    }
  }, [statsError]);

  // Debug logging
  useEffect(() => {
    console.log('Stats loading:', statsLoading);
    console.log('Stats error:', statsError);
    console.log('Stats data:', stats);
    console.log('Overview parsed:', overview);
  }, [stats, statsLoading, statsError, overview]);

  const statCards = [
    {
      name: 'Total Requests',
      value: overview.totalRequests || 0,
      icon: ChartBarIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      name: 'Pending Review',
      value: overview.pendingRequests || 0,
      icon: ClockIcon,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      name: 'Approved',
      value: overview.approvedRequests || 0,
      icon: CheckCircleIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      name: 'Total Mitra',
      value: overview.totalMitra || 0,
      icon: UserGroupIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ];

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'requests', name: 'All Requests', icon: DocumentTextIcon },
    { id: 'deleted', name: 'Deleted Requests', icon: TrashIcon },
    ...(user?.role !== 'support' ? [{ id: 'users', name: 'Manage Mitra', icon: UserGroupIcon }] : []),
    ...(user?.role === 'superadmin' ? [{ id: 'rbac', name: 'RBAC Management', icon: UserPlusIcon }] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="h-8 w-8 mr-3">
                <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Garuda Media" className="w-full h-full object-contain" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">
                Admin Panel - Garuda Media
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Welcome, <span className="font-medium">{user?.username}</span>
              </div>
              <div className="flex items-center space-x-2">
                {user?.role === 'superadmin' && (
                  <a
                    href="/noc"
                    className="btn-secondary text-sm flex items-center"
                    title="Switch to NOC Dashboard"
                  >
                    <ArrowRightIcon className="h-4 w-4 mr-1" />
                    NOC Panel
                  </a>
                )}
                {user?.role !== 'support' && (
                  <button
                    onClick={() => setShowCreateAdmin(true)}
                    className="btn-primary text-sm flex items-center"
                    title="Create New Admin User"
                  >
                    <UserPlusIcon className="h-4 w-4 mr-1" />
                    Add Admin
                  </button>
                )}
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="btn-secondary text-sm"
                >
                  Change Password
                </button>
                <button
                  onClick={logout}
                  className="btn-secondary text-sm"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Admin Dashboard</h2>
            <p className="mt-1 text-sm text-gray-600">
              Manage bandwidth upgrade requests and system users
            </p>
          </div>
          {activeTab === 'overview' && (
            <button
              onClick={handleRefreshStats}
              disabled={statsLoading}
              className="btn-secondary disabled:opacity-50"
            >
              {statsLoading ? 'Refreshing...' : 'Refresh Stats'}
            </button>
          )}
        </div>

        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6">
            {statsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                <p className="ml-4 text-gray-600">Loading statistics...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat) => (
                  <div key={stat.name} className="card p-6">
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 ${stat.bgColor} p-3 rounded-lg`}>
                        <stat.icon className={`h-6 w-6 ${stat.color}`} />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                        <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="card">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Recent Requests</h3>
                <button
                  onClick={() => setActiveTab('requests')}
                  className="btn-primary text-sm"
                >
                  View All Requests
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mitra
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Account ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bandwidth
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quick Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {recentRequests.length > 0 ? recentRequests.map((request) => (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {request.user?.company_name || request.user?.username}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {request.account_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {request.current_bandwidth} → {request.requested_bandwidth}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={getStatusBadge(request.status)}>
                            {getStatusLabel(request.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(request.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setShowDetails(showDetails === request.id ? null : request.id)}
                              className="text-primary-600 hover:text-primary-900"
                              title="View Details"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            
                            {request.status === 'approved' && (
                              <button
                                onClick={() => handleViewDocument(request)}
                                className="text-green-600 hover:text-green-900"
                                title="View Document"
                              >
                                <DocumentTextIcon className="h-4 w-4" />
                              </button>
                            )}

                            {request.status === 'pending' && user?.role !== 'support' && (
                              <>
                                <button
                                  onClick={() => handleStatusUpdate(request.id, 'admin_approved')}
                                  disabled={updateLoading}
                                  className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                  title="Approve"
                                >
                                  <CheckCircleIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    const comments = prompt('Rejection reason (optional):');
                                    if (comments !== null) {
                                      handleStatusUpdate(request.id, 'rejected', comments);
                                    }
                                  }}
                                  disabled={updateLoading}
                                  className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                  title="Reject"
                                >
                                  <XCircleIcon className="h-4 w-4" />
                                </button>
                              </>
                            )}

                            {user?.role !== 'support' && (
                              <button
                                onClick={() => handleDeleteRequest(request)}
                                disabled={deleteLoading}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                title="Delete Request"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                          No recent requests found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Details row for overview table */}
              {recentRequests.map((request) => 
                showDetails === request.id && (
                  <div key={`${request.id}-details`} className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Justification</h4>
                        <p className="text-gray-700 whitespace-pre-wrap">{request.justification}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Site Addresses</h4>
                        <div className="space-y-2">
                          <div>
                            <span className="font-medium">Site A:</span>
                            <p className="text-gray-700">{request.site_a_address}</p>
                          </div>
                          <div>
                            <span className="font-medium">Site B:</span>
                            <p className="text-gray-700">{request.site_b_address}</p>
                          </div>
                        </div>
                      </div>
                      {request.admin_comments && (
                        <div className="md:col-span-2">
                          <h4 className="font-medium text-gray-900 mb-2">Admin Comments</h4>
                          <p className="text-gray-700 bg-yellow-50 p-3 rounded">{request.admin_comments}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {activeTab === 'requests' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by Account ID, Order ID, or Mitra..."
                    className="form-input pl-10"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <FunnelIcon className="h-5 w-5 text-gray-400" />
                  <select
                    className="form-input w-auto"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="card overflow-hidden">
              {requestsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                  <p className="ml-4 text-gray-600">Loading requests...</p>
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No requests found.</p>
                  <p className="text-gray-400 text-sm mt-2">
                    {search || statusFilter !== 'all' 
                      ? 'Try adjusting your filters or search terms.'
                      : 'No upgrade requests have been submitted yet.'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Mitra Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Request Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Bandwidth
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {requests.map((request) => (
                        <React.Fragment key={request.id}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {request.user?.company_name || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {request.user?.username}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {request.user?.contact_person}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  Account: {request.account_id}
                                </p>
                                {request.order_id && (
                                  <p className="text-sm text-gray-500">
                                    Order: {request.order_id}
                                  </p>
                                )}
                                <p className="text-sm text-gray-500">
                                  {request.service_type}
                                </p>
                                <p className="text-sm text-gray-400">
                                  {formatDate(request.createdAt)}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                <span className="font-medium">{request.current_bandwidth}</span>
                                <span className="text-gray-500 mx-2">→</span>
                                <span className="font-medium text-green-600">{request.requested_bandwidth}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={getStatusBadge(request.status)}>
                                {getStatusLabel(request.status)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => setShowDetails(showDetails === request.id ? null : request.id)}
                                  className="text-primary-600 hover:text-primary-900"
                                >
                                  <EyeIcon className="h-5 w-5" />
                                </button>
                                
                                {request.status === 'approved' && (
                                  <button
                                    onClick={() => handleViewDocument(request)}
                                    className="text-green-600 hover:text-green-900"
                                    title="View Document"
                                  >
                                    <DocumentTextIcon className="h-5 w-5" />
                                  </button>
                                )}

                                {request.status === 'pending' && user?.role !== 'support' && (
                                  <>
                                    <button
                                      onClick={() => {
                                        if (window.confirm(`Approve request for ${request.account_id}?`)) {
                                          handleStatusUpdate(request.id, 'admin_approved');
                                        }
                                      }}
                                      disabled={updateLoading}
                                      className="btn-success text-xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {updateLoading ? 'Processing...' : 'Approve'}
                                    </button>
                                    <button
                                      onClick={() => {
                                        const comments = prompt(`Rejection reason for ${request.account_id} (optional):`);
                                        if (comments !== null) {
                                          if (window.confirm('Confirm rejection of this request?')) {
                                            handleStatusUpdate(request.id, 'rejected', comments);
                                          }
                                        }
                                      }}
                                      disabled={updateLoading}
                                      className="btn-danger text-xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {updateLoading ? 'Processing...' : 'Reject'}
                                    </button>
                                  </>
                                )}

                                {user?.role !== 'support' && (
                                  <button
                                    onClick={() => handleDeleteRequest(request)}
                                    disabled={deleteLoading}
                                    className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                    title="Delete Request"
                                  >
                                    <TrashIcon className="h-5 w-5" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          
                          {showDetails === request.id && (
                            <tr>
                              <td colSpan={5} className="px-6 py-4 bg-gray-50">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Justification</h4>
                                    <p className="text-gray-700 whitespace-pre-wrap">{request.justification}</p>
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Site Addresses</h4>
                                    <div className="space-y-2">
                                      <div>
                                        <span className="font-medium">Site A:</span>
                                        <p className="text-gray-700">{request.site_a_address}</p>
                                      </div>
                                      <div>
                                        <span className="font-medium">Site B:</span>
                                        <p className="text-gray-700">{request.site_b_address}</p>
                                      </div>
                                    </div>
                                  </div>
                                  {request.admin_comments && (
                                    <div className="md:col-span-2">
                                      <h4 className="font-medium text-gray-900 mb-2">Admin Comments</h4>
                                      <p className="text-gray-700 bg-yellow-50 p-3 rounded">{request.admin_comments}</p>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {pagination.pages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="btn-secondary"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                      disabled={currentPage === pagination.pages}
                      className="btn-secondary"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{((currentPage - 1) * 10) + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * 10, pagination.total)}
                        </span> of{' '}
                        <span className="font-medium">{pagination.total}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                          const page = i + 1;
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === page
                                  ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                          disabled={currentPage === pagination.pages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Mitra User Management</h3>
                  <p className="text-sm text-gray-600">Manage mitra users, reset passwords, and view user details</p>
                </div>
                <button
                  onClick={() => setShowMitraManagement(true)}
                  className="btn-primary flex items-center"
                >
                  <UserGroupIcon className="h-4 w-4 mr-2" />
                  Open Management Panel
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <UserGroupIcon className="h-8 w-8 text-blue-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-blue-600">Total Mitra</p>
                      <p className="text-2xl font-semibold text-blue-900">{overview.totalMitra || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-8 w-8 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-600">Active Requests</p>
                      <p className="text-2xl font-semibold text-green-900">{overview.pendingRequests || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-8 w-8 text-yellow-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-yellow-600">Total Requests</p>
                      <p className="text-2xl font-semibold text-yellow-900">{overview.totalRequests || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="text-md font-medium text-gray-900 mb-3">Available Actions</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900">User Management</h5>
                    <p className="text-sm text-gray-600 mt-1">Create, edit, and delete mitra user accounts</p>
                    <ul className="text-xs text-gray-500 mt-2 space-y-1">
                      <li>• Add new mitra users</li>
                      <li>• Edit user information</li>
                      <li>• Manage account details</li>
                    </ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900">Password Management</h5>
                    <p className="text-sm text-gray-600 mt-1">Reset passwords and manage user authentication</p>
                    <ul className="text-xs text-gray-500 mt-2 space-y-1">
                      <li>• Reset user passwords</li>
                      <li>• Force password changes</li>
                      <li>• Manage user access</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Bandwidth Package Management</h3>
                  <p className="text-sm text-gray-600">Manage bandwidth packages, pricing, and availability</p>
                </div>
                <button
                  onClick={() => setShowPackageManagement(true)}
                  className="btn-primary flex items-center"
                >
                  <ChartBarIcon className="h-4 w-4 mr-2" />
                  Manage Packages
                </button>
              </div>
              
              <div className="mt-6">
                <h4 className="text-md font-medium text-gray-900 mb-3">Package Management Features</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900">Package Configuration</h5>
                    <p className="text-sm text-gray-600 mt-1">Create and edit bandwidth packages with pricing</p>
                    <ul className="text-xs text-gray-500 mt-2 space-y-1">
                      <li>• Add new bandwidth packages</li>
                      <li>• Set pricing for each package</li>
                      <li>• Configure package availability</li>
                    </ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900">Pricing Management</h5>
                    <p className="text-sm text-gray-600 mt-1">Update prices and manage package status</p>
                    <ul className="text-xs text-gray-500 mt-2 space-y-1">
                      <li>• Update package pricing</li>
                      <li>• Enable/disable packages</li>
                      <li>• View package statistics</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rbac' && user?.role === 'superadmin' && (
          <div className="space-y-6">
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">RBAC Management</h3>
                  <p className="text-sm text-gray-600">Manage roles, permissions, and access control system</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <UserPlusIcon className="h-8 w-8 text-blue-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-blue-600">Current System Roles</p>
                      <p className="text-2xl font-semibold text-blue-900">5</p>
                      <p className="text-xs text-blue-500">mitra, support, noc, admin, superadmin</p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <ChartBarIcon className="h-8 w-8 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-600">RBAC Status</p>
                      <p className="text-2xl font-semibold text-green-900">Active</p>
                      <p className="text-xs text-green-500">Role-based access control enabled</p>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-8 w-8 text-purple-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-purple-600">API Endpoints</p>
                      <p className="text-2xl font-semibold text-purple-900">/api/superadmin/*</p>
                      <p className="text-xs text-purple-500">RBAC management endpoints</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="text-md font-medium text-gray-900 mb-3">RBAC Management Features</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900">Role Management</h5>
                    <p className="text-sm text-gray-600 mt-1">Manage system roles and their hierarchy levels</p>
                    <ul className="text-xs text-gray-500 mt-2 space-y-1">
                      <li>• GET /api/superadmin/roles - List all roles</li>
                      <li>• POST /api/superadmin/roles - Create new role</li>
                      <li>• PUT /api/superadmin/roles/:id - Update role</li>
                      <li>• DELETE /api/superadmin/roles/:id - Delete role</li>
                    </ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900">Permission Management</h5>
                    <p className="text-sm text-gray-600 mt-1">Configure permissions and resource access</p>
                    <ul className="text-xs text-gray-500 mt-2 space-y-1">
                      <li>• GET /api/superadmin/permissions - List permissions</li>
                      <li>• Role-permission assignments</li>
                      <li>• Resource-based access control</li>
                      <li>• API endpoint protection</li>
                    </ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900">Current Role Hierarchy</h5>
                    <p className="text-sm text-gray-600 mt-1">System role levels and permissions</p>
                    <ul className="text-xs text-gray-500 mt-2 space-y-1">
                      <li>• Level 5: superadmin (all permissions)</li>
                      <li>• Level 4: admin (management permissions)</li>
                      <li>• Level 3: noc (workflow permissions)</li>
                      <li>• Level 2: support (read-only permissions)</li>
                      <li>• Level 1: mitra (user permissions)</li>
                    </ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900">Security Features</h5>
                    <p className="text-sm text-gray-600 mt-1">Advanced security and audit capabilities</p>
                    <ul className="text-xs text-gray-500 mt-2 space-y-1">
                      <li>• Role-based route protection</li>
                      <li>• API endpoint authorization</li>
                      <li>• Permission inheritance</li>
                      <li>• Audit logging and monitoring</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">RBAC API Access</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>Use the superadmin API endpoints to manage RBAC programmatically. All endpoints require superadmin authentication.</p>
                      <p className="mt-1">Configuration file: <code className="text-xs bg-yellow-100 px-1 rounded">backend/config/rbac-config.js</code></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {activeTab === 'deleted' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <DeletedRequestsTab
            deletedRequests={deletedRequests}
            deletedRequestsLoading={deletedRequestsLoading}
            deletedSearch={deletedSearch}
            setDeletedSearch={setDeletedSearch}
            deletedPagination={deletedPagination}
            deletedCurrentPage={deletedCurrentPage}
            setDeletedCurrentPage={setDeletedCurrentPage}
            handleRestoreRequest={handleRestoreRequest}
            restoreLoading={restoreLoading}
            getStatusBadge={getStatusBadge}
            getStatusLabel={getStatusLabel}
          />
        </div>
      )}

      {showDocument && selectedRequest && (
        <AcceptanceDocument
          request={selectedRequest}
          onClose={() => {
            setShowDocument(false);
            setSelectedRequest(null);
          }}
        />
      )}
      
      {showChangePassword && (
        <ChangePasswordModal
          onClose={handlePasswordChangeClose}
          isForced={forcePasswordChange}
        />
      )}

      {showDeleteModal && requestToDelete && (
        <DeleteRequestModal
          request={requestToDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setRequestToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          loading={deleteLoading}
        />
      )}

      {showCreateAdmin && (
        <CreateAdminModal
          onClose={() => setShowCreateAdmin(false)}
          onSuccess={handleCreateAdminSuccess}
        />
      )}

      {showMitraManagement && (
        <MitraManagementModal
          onClose={() => setShowMitraManagement(false)}
          onSuccess={handleMitraManagementSuccess}
        />
      )}

      {showPackageManagement && (
        <PackageManagementModal
          onClose={() => setShowPackageManagement(false)}
          onSuccess={() => {
            // Refresh stats if needed
            if (activeTab === 'overview') {
              refetchStats();
            }
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;